// Single source of truth for design values. Deliberately library-agnostic: no antd import, no antd
// vocabulary. Adapters translate these into ConfigProvider / Tailwind / CSS-variable shapes, so
// swapping the component library means rewriting an adapter, not this file.
//
// Values were authored by running antd's getDesignToken() against the config in use on 2026-08-05
// and freezing the curated subset. That makes adoption a no-op: what ships today is what shipped
// yesterday. Figma values land later as a diff to this file alone.

// 4px grid. antd derives its own 9-step scale from unit+step, so the adapter passes only those two;
// the scale below exists for Tailwind and CSS-variable consumers.
export const SPACE = {
  UNIT: 4,
  STEP: 4,
  XXS: 4,
  XS: 8,
  SM: 12,
  MD: 16,
  LG: 24,
  XL: 32,
  XXL: 48,
} as const;

// Spacing and type roles whose value depends on layout density: COMPACT for touch, COMFORTABLE for
// pointer. Keys are lowercase because they ARE the generated Tailwind class names (`p-card`).
export const DENSITY = {
  SPACE: {
    gutter: { COMPACT: 16, COMFORTABLE: 24 },
    card: { COMPACT: 16, COMFORTABLE: 24 },
    stack: { COMPACT: 12, COMFORTABLE: 24 },
    'stack-lg': { COMPACT: 16, COMFORTABLE: 32 },
    section: { COMPACT: 16, COMFORTABLE: 24 },
  },
  // A 24/20 ramp reads as one level once the columns stack, so the compact steps pull further apart.
  FONT: {
    'page-title': { COMPACT: 22, COMFORTABLE: 24 },
    section: { COMPACT: 17, COMFORTABLE: 20 },
  },
  LINE_HEIGHT: {
    'page-title': { COMPACT: 28, COMFORTABLE: 32 },
    section: { COMPACT: 24, COMFORTABLE: 28 },
  },
  // Matches the `sm:` prefix the call sites already use for layout changes.
  BREAKPOINT: '640px',
} as const;

// `md` is the seed antd derives xs/sm/lg from. Today's derivation: 6 -> xs 2, sm 4, lg 8.
export const RADIUS = {
  XS: 2,
  SM: 4,
  MD: 6,
  LG: 8,
} as const;

export const BORDER = {
  WIDTH: 1,
  STYLE: 'solid',
} as const;

export const CONTROL = {
  HEIGHT: { XS: 16, SM: 24, MD: 32, LG: 40 },
} as const;

export const FONT = {
  // Loaded from Google Fonts in index.html at these weights only.
  FAMILY: {
    BODY: "'Inter', sans-serif",
    HEADING: "'Poppins', sans-serif",
  },
  // `md` is the seed; antd derives the rest of its scale from it.
  // SM/MD/LG/XL line up with what antd derives — SM 12, base 14, LG 16, XL 20 — so a reader does not
  // have to translate between the two scales. XS and XXL have no antd counterpart: XS is the 10px the
  // app already spends 35-odd `text-[10px]` on, XXL the app's own large step.
  SIZE: {
    XS: 10,
    SM: 12,
    MD: 14,
    LG: 16,
    XL: 20,
    XXL: 22,
  },
  HEADING: { H1: 38, H2: 30, H3: 24, H4: 20, H5: 16 },
  LINE_HEIGHT: { XS: 1.6666666666666667, MD: 1.5714285714285714, LG: 1.5 },
  WEIGHT: { REGULAR: 400, MEDIUM: 500, SEMIBOLD: 600 },
} as const;

// Primitives from the design file's extracted palettes (Daily Hub / Doc Detail View / Textbox), named
// exactly as those frames name them so a new swatch is a new line and there is no translation table.
// Scoped to what the document viewer needs — the rest of the ramps land when a surface uses them.
//
// Nothing consumes this directly. The semantic `COLOR` map below is the contract; adapters read only
// that, so adding a primitive emits no CSS.
export const PALETTE = {
  GRAY: {
    30: '#f9fafc',
    75: '#f6f6f6',
    100: '#f1f1f1',
    125: '#f0f0f0',
    250: '#d9d9d9',
    350: '#bfbfbf',
    450: '#8c8c8c',
    600: '#64748b',
    700: '#374151',
    850: '#1f1f1f',
  },
  BLUE: {
    50: '#e6f4ff',
    100: '#bae0ff',
    500: '#3079eb',
    600: '#1677ff',
    700: '#0958d9',
    850: '#143460',
    900: '#002c8c',
  },
  RED: {
    50: '#fff2f0',
    200: '#ffccc7',
    600: '#d9363e',
    800: '#a8071a',
  },
  GREEN: { 600: '#389e0d', 700: '#237804' },
  ORANGE: { 600: '#fa8c16' },
  // The Daily Hub's notes affordance fills with these when a transaction carries a note.
  YELLOW: { 200: '#ffffb8', 700: '#d4b106' },
  INK: {
    900: '#0B1220',
    800: '#1A2436',
    700: '#2B3340',
    600: '#57677E',
    500: '#7B8798',
    400: '#A6AEBC',
    300: '#C9CED8',
    200: '#E3E7EE',
    100: '#F1F3F7',
    50: '#F7F9FC',
  },
} as const;

// Text, BORDER, bg and fill neutrals are alpha-on-black, not flat hex. Keeping the alpha is
// deliberate: flattening them to their on-white equivalents breaks compositing over every grey
// surface in the app (#f5f5f5 layouts, #F6F8FA pages, hover fills).
export const COLOR = {
  BRAND: {
    PRIMARY: '#2F78EB',
    DARK: '#003F88',
    DARKER: '#002652',
  },

  TEXT: {
    BASE: '#000',
    DEFAULT: 'rgba(0,0,0,0.88)',
    // Flat rather than alpha: the design file specifies these two directly, and every surface they sit
    // on in the viewer is white or near-white, so nothing is lost by not compositing.
    SECONDARY: PALETTE.GRAY[700],
    TERTIARY: PALETTE.GRAY[600],
    QUATERNARY: 'rgba(0,0,0,0.25)',
    ICON: 'rgba(0,0,0,0.45)',
    ON_BRAND: '#ffffff',
  },

  BORDER: {
    DEFAULT: '#d9d9d9',
    SUBTLE: '#f0f0f0',
  },

  BG: {
    BASE: '#fff',
    CONTAINER: '#ffffff',
    ELEVATED: '#ffffff',
    LAYOUT: '#f5f5f5',
    SPOTLIGHT: 'rgba(0,0,0,0.85)',
  },

  FILL: {
    DEFAULT: 'rgba(0,0,0,0.15)',
    SECONDARY: 'rgba(0,0,0,0.06)',
    TERTIARY: 'rgba(0,0,0,0.04)',
    QUATERNARY: 'rgba(0,0,0,0.02)',
  },

  SEMANTIC: {
    SUCCESS: '#52c41a',
    WARNING: '#faad14',
    ERROR: '#ff4d4f',
    INFO: '#1677ff',
    LINK: '#1677ff',
  },

  // Fills read one step off the design file by name but land on its values by sight: secondary is
  // #F0F0F0 against its #f1f1f1 tag fill, tertiary #F5F5F5 against its #f6f6f6 canvas, quaternary
  // #FAFAFA against its #f9fafc sidebar. Renaming to match would be worse than leaving them — its
  // `colorFillSecondary` is a near-white tint, and IconButton's active press needs a visible one.

  // Transaction/document status PALETTE, carried over from tailwind.config.js unchanged.
  STATUS: {
    INACTIVE: '#EDEBEB',
    PENDING: '#FFD198',
    PROGRESS: '#A0FAFF',
    READY: '#7FD6FF',
    COMPLETE: '#6CFF9D',
    ERROR: '#FFA8A8',
    UTILITY: '#E2FFA8',
  },
} as const;

// Only two levels, deliberately. antd ships three global shadow names but `boxShadow` and
// `boxShadowSecondary` are byte-identical, and `boxShadowCard` is a Card component token rather than
// a global one. A genuine third level needs a design decision — none of the Figma nodes specify a
// shadow at all, so inventing one here would be guessing.
export const ELEVATION = {
  LOW: '0 1px 2px 0 rgba(0, 0, 0, 0.03), 0 1px 6px -1px rgba(0, 0, 0, 0.02), 0 2px 4px 0 rgba(0, 0, 0, 0.02)',
  MEDIUM:
    '0 6px 16px 0 rgba(0, 0, 0, 0.08), 0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 9px 28px 8px rgba(0, 0, 0, 0.05)',
} as const;

// Values that disagree across the app today. Recorded rather than merged so the foundation ships with
// zero visual change; each is resolved in its own reviewable PR. Do not read these to style anything.
export const UNRESOLVED_CONFLICTS = {
  // `COLOR.BRAND.PRIMARY` is what antd renders. Tailwind's `bg-primary` resolves to --primary
  // (#1677FF), so "primary" buttons ship in two different blues.
  BRAND_PRIMARY: {
    ANTD: '#2F78EB',
    TAILWIND_CSS_VAR: '#1677FF',
    PUBLIC_FORMS: '#1B9AD8',
    LEGACY: '#144B98',
  },
  // `COLOR.BORDER.DEFAULT` is antd's. Tailwind's --border and the stock palette disagree.
  BORDER: {
    ANTD: '#d9d9d9',
    TAILWIND_CSS_VAR: '#e2e8f0',
    TAILWIND_STOCK_GRAY200: '#e5e7eb',
  },
  // index.css forces 13px on every table, contradicting FONT.SIZE.MD.
  TABLE_FONT_SIZE: { token: 14, globalOverride: 13 },
  // The design file lists two values for the same alias, so neither can be adopted. `text.icon` stays
  // on rgba(0,0,0,0.45) — #8C8C8C, which is one of the two — until design collapses them.
  AMBIGUOUS_IN_DESIGN: {
    COLOR_ICON: ['#8c8c8c', '#64748b'],
    COLOR_BORDER: ['#d9d9d9', '#d1d5db'],
    COLOR_BORDER_SECONDARY: ['#f0f0f0', '#e2e8f0'],
  },
  // One character apart and imperceptible, but `COLOR.BRAND.PRIMARY` is also hardcoded as the app-wide
  // antd colorPrimary in ProtectedShell, so changing it moves every antd surface for no visible gain.
  BRAND_PRIMARY_VS_DESIGN: { token: '#2F78EB', designBlue500: '#3079EB' },
  // antd currently renders its stock system stack. `.custom-fonts-enabled` (env-gated, on) forces
  // FONT.FAMILY.BODY onto everything with !important, so setting the antd token is a no-op today.
  FONT_FAMILY: {
    ANTD_RUNTIME: 'system stack',
    FORCED_BY_GLOBAL_CSS: FONT.FAMILY.BODY,
  },
} as const;
